import { useCallback, useMemo, useState, type ReactNode } from 'react';

import {
  DesktopTargetViewSelectionContext,
  type DesktopTargetSticky,
} from './desktop-target-view-selection-context';

type Handoff = { scope: string; override: DesktopTargetSticky };

type ViewSelectionState = {
  scope: string;
  override: DesktopTargetSticky;
  /** An in-place create bound this project while Home was mounted; its choice rides along. */
  carry: { projectId: string; override: DesktopTargetSticky } | null;
  /** The seeded handoff already promoted once; a later visit must not revive it. */
  consumedHandoffScope: string | null;
};

const isHomeScope = (scope: string) => scope.startsWith('home:');

/**
 * Pure: the same inputs always yield the same next state. This runs during
 * render (see below), and StrictMode invokes render twice — any ref mutation
 * here would be consumed by the first pass and missing from the second.
 */
const reconcileScope = (
  state: ViewSelectionState,
  scope: string,
  handoff: Handoff | undefined
): ViewSelectionState => {
  const projectId = scope.split(':')[0];
  const fromHome = isHomeScope(state.scope);
  const boundHere = fromHome && state.carry?.projectId === projectId;
  const promoteSeeded =
    fromHome && handoff?.scope === scope && state.consumedHandoffScope !== scope;
  return {
    scope,
    override: boundHere
      ? (state.carry?.override ?? null)
      : promoteSeeded
        ? handoff.override
        : null,
    carry: null,
    consumedHandoffScope: promoteSeeded ? scope : state.consumedHandoffScope,
  };
};

/** A manual pick belongs to the mounted view, never to the user or browser. */
export function DesktopTargetViewSelectionProvider({
  children,
  scope,
  handoff,
}: {
  children: ReactNode;
  scope: string;
  /** The opening turn's choice when this mounted home view becomes a project. */
  handoff?: Handoff;
}) {
  const [state, setState] = useState<ViewSelectionState>(() => {
    // A route/layout remount can replace the provider between create and the
    // first project render. The pending opening turn is the handoff in that case.
    const fromHandoff = handoff?.scope === scope;
    return {
      scope,
      override: fromHandoff ? handoff.override : null,
      carry: null,
      consumedHandoffScope: fromHandoff ? scope : null,
    };
  });
  // The route can swap project ids without remounting its chat. Clear during
  // render so navigating back cannot revive the previous project's choice.
  if (state.scope !== scope) {
    setState(reconcileScope(state, scope, handoff));
  }
  const override = state.scope === scope ? state.override : null;
  const [attachedRoutineId, setAttachedRoutineId] = useState<string | null>(null);
  const setOverride = useCallback(
    (value: DesktopTargetSticky) =>
      setState((current) => ({ ...current, scope, override: value })),
    [scope]
  );
  const reset = useCallback(
    () => setState((current) => ({ ...current, scope, override: null })),
    [scope]
  );
  const carryToProject = useCallback(
    (projectId: string) =>
      setState((current) => ({
        ...current,
        carry: { projectId, override: current.override },
      })),
    []
  );
  const value = useMemo(
    () => ({
      override,
      setOverride,
      carryToProject,
      reset,
      attachedRoutineId,
      setAttachedRoutineId,
    }),
    [override, reset, setOverride, carryToProject, attachedRoutineId]
  );

  return (
    <DesktopTargetViewSelectionContext.Provider value={value}>
      {children}
    </DesktopTargetViewSelectionContext.Provider>
  );
}
